import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, FolderOpen, Loader2, Table as TableIcon, ImageIcon, Pencil, Printer, FolderInput, X, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KbEditDialog } from "@/components/KbEditDialog";
import { KbIcon } from "@/components/KbIcon";
import { KbNewPill } from "@/components/KbNewPill";
import { KbSnippetPill } from "@/components/KbSnippetPill";
import { kbHighlight } from "@/lib/kbHighlight";
import { KbStyleInjector } from "@/lib/kbSettings";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";

function freshness(iso) {
  // Imported rows store "2025-08-20 15:42:33 UTC"; normalise before parsing.
  const s = String(iso || "");
  const d = new Date(s.includes("T") ? s : s.replace(" UTC", "Z").replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return { label: "", stale: false };
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return { label: "today", stale: false };
  if (days === 1) return { label: "yesterday", stale: false };
  if (days < 30) return { label: `${days}d ago`, stale: false };
  if (days < 365) return { label: `${Math.round(days / 30)}mo ago`, stale: days > 180 };
  return { label: `${Math.floor(days / 365)}y ago`, stale: true };
}


export default function KBCategory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tree, setTree] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allCats, setAllCats] = useState([]);
  const [target, setTarget] = useState("");
  const [moving, setMoving] = useState(false);
  const [filter, setFilter] = usePersistedState(`kb-category.${slug}.filter`, "");
  const [sortBy, setSortBy] = usePersistedState(`kb-category.${slug}.sort`, "az");
  const [results, setResults] = useState(null); // full-text (title+body) search, subtree-scoped
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setData(null); setSelectMode(false); setSelected(new Set()); setResults(null);
    api.get(`/kb/categories/${slug}`).then((r) => {
      setData(r.data);
    }).catch((e) => setData({ error: true, forbidden: e.response?.status === 403 }));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter 120 — the full category list is only needed for the "move to…"
  // dropdown, so fetch it when the editor actually enters select mode
  // (browsing categories quickly used to hammer this rate-limited endpoint).
  useEffect(() => {
    if (!selectMode || allCats.length) return;
    api.get("/kb/manage/categories").then((mr) => setAllCats(mr.data.categories)).catch(() => {});
  }, [selectMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced full-text filter: matches article titles AND body across this
  // category's subtree (server-side) so users can find articles by content.
  useEffect(() => {
    const term = filter.trim();
    const catId = data?.category?.cat_id;
    if (!catId || term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/kb/search", { params: { q: term, category_id: catId, limit: 50 } })
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [filter, data?.category?.cat_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const doMove = async () => {
    if (!target) { toast.error("Pick a destination category"); return; }
    setMoving(true);
    try {
      const { data: res } = await api.post("/kb/articles/move", {
        article_ids: [...selected], from_cat_id: data.category.cat_id, target_cat_id: target });
      toast.success(`Moved ${res.moved} article(s) to “${res.target}”`);
      setSelectMode(false); setSelected(new Set()); setTarget("");
      api.get(`/kb/categories/${slug}`).then((r) => setData(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMoving(false); }
  };

  // Iter 239 — publish the category chain to the single global breadcrumb.
  usePageBreadcrumb(useMemo(() => {
    if (!data || data.error) return null;
    const current = data.category.name.trim();
    return [
      { label: "Knowledge Base", to: "/kb" },
      ...(data.breadcrumbs || []).filter((t) => t.name.trim() !== current && t.name.trim() !== "Knowledge Base")
        .map((t) => ({ label: t.name.trim(), to: `/kb/c/${t.slug}` })),
      { label: current },
    ];
  }, [data]));

  if (!data) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;
  if (data.error) return (
    <div className="kb-surface" data-testid="kb-category-missing">
      <div className="mx-auto mt-16 max-w-md rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-10 text-center">
        <FolderOpen className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
        {data.forbidden ? (
          <>
            <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">This category is internal to staff only.</p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">You don&apos;t have access to internal content.</p>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Category not found.</p>
        )}
        <button onClick={() => navigate("/kb")} className="mt-4 text-sm font-medium text-primary hover:underline">← Back to the Knowledge Base</button>
      </div>
    </div>
  );

  const { category, children, articles } = data;
  const q = filter.trim();
  const searchMode = results !== null;
  const visible = searchMode
    ? results
    : articles
        .filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => {
          if (sortBy === "views") return (b.hj_views || 0) - (a.hj_views || 0);
          if (sortBy === "new") return (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0) || a.name.localeCompare(b.name);
          return a.name.localeCompare(b.name);
        });
  const highlight = (text) => (searchMode ? kbHighlight(text, filter.trim()) : text);
  return (
    <div data-testid="kb-category-page" className="kb-surface">
      <KbStyleInjector />
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <KbIcon icon={category.icon} name={category.name} className="h-6 w-6" />
        </span>
        <div>
          <h1 className="flex flex-wrap items-center gap-2 font-heading text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="kb-category-title">{category.name.trim()}{category.is_new && <KbNewPill />}</h1>
          {category.description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{category.description}</p>}
          <span className="mt-2 inline-block rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400"
            data-testid="kb-category-scope">
            {data.category.scope === "external" ? "All scopes" : "Staff only"}
          </span>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" data-testid="kb-print-pack-button"
            onClick={() => navigate(`/kb/c/${slug}/print`)} title="Print this category as a booklet">
            <Printer className="h-4 w-4" /> Print pack
          </Button>
          {data.can_edit && (
            <Button size="sm" data-testid="kb-category-new-article-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => {
                if (!tree) api.get("/kb/tree").then((r) => setTree(r.data || [])).catch(() => setTree([]));
                setCreating(true);
              }}>
              <Plus className="h-4 w-4" /> New article
            </Button>
          )}
          {data.can_edit && (
            <Button variant="outline" size="sm" data-testid="kb-edit-category-button"
              onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          )}
        </div>
      </div>

      {children.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Browse sub-categories</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="kb-subcategories">
          {children.map((c) => (
            <button key={c.cat_id} data-testid={`kb-subcategory-${c.slug}`} onClick={() => navigate(`/kb/c/${c.slug}`)}
              className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-800">
              <FolderOpen className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-700 dark:text-slate-200">{c.name.trim()}</span>
                <span className="flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500" data-testid={`kb-subcategory-meta-${c.slug}`}>
                  <span>{c.article_count_deep} {c.article_count_deep === 1 ? "article" : "articles"}</span>
                  {c.last_updated && freshness(c.last_updated).label && (
                    <>
                      <span className="text-slate-300 dark:text-slate-600">·</span>
                      <span className={freshness(c.last_updated).stale ? "text-amber-600" : ""}>
                        updated {freshness(c.last_updated).label}
                      </span>
                    </>
                  )}
                </span>
              </span>
            </button>
          ))}
          </div>
        </section>
      )}

      {/* Iter 120 — a category that only holds sub-categories doesn't need an
          empty "Articles (0)" block; the sub-category grid above says it all. */}
      {(articles.length > 0 || children.length === 0) && (
      <div className="mt-8">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500" data-testid="kb-articles-count">
            Articles in this category ({searchMode ? `${visible.length} match${visible.length === 1 ? "" : "es"}` : (q ? `${visible.length} of ${articles.length}` : articles.length)})
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 dark:text-slate-500" />}
            {articles.length > 5 && !selectMode && (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input value={filter} onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search title or content…" data-testid="kb-article-filter"
                    className="h-8 w-44 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-8 pr-6 text-xs text-slate-700 dark:text-slate-200 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-60" />
                  {filter && (
                    <button type="button" data-testid="kb-article-filter-clear" onClick={() => setFilter("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
                  )}
                </div>
                {!searchMode && (
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="kb-article-sort"
                    className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-xs text-slate-600 dark:text-slate-300 focus:outline-none">
                    <option value="az">A → Z</option>
                    <option value="views">Most viewed</option>
                    <option value="new">New first</option>
                  </select>
                )}
              </>
            )}
            {data.can_edit && articles.length > 0 && (
              selectMode ? (
                <button onClick={() => { setSelectMode(false); setSelected(new Set()); }} data-testid="kb-move-cancel"
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700">
                  <X className="h-3.5 w-3.5" /> Cancel
                </button>
              ) : (
                <button onClick={() => { setSelectMode(true); setFilter(""); }} data-testid="kb-move-start"
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                  <FolderInput className="h-3.5 w-3.5" /> Move articles
                </button>
              )
            )}
          </div>
        </div>
        {articles.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 py-12 text-center text-sm text-slate-400 dark:text-slate-500"
            data-testid="kb-category-empty">
            No articles filed directly in this category — check the sub-categories above.
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 py-10 text-center text-sm text-slate-400 dark:text-slate-500"
            data-testid="kb-article-filter-empty">
            {searching ? "Searching…" : <>No articles match “{filter}”.</>}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            {visible.map((a) => (
              selectMode ? (
                <label key={a.article_id} data-testid={`kb-move-row-${a.slug}`}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <input type="checkbox" checked={selected.has(a.article_id)} onChange={() => toggleSel(a.article_id)}
                    data-testid={`kb-move-check-${a.slug}`} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                  <FileText className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{a.name}</span>
                  {a.is_draft && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Draft</span>}
                </label>
              ) : (
                <button key={a.article_id} data-testid={`kb-article-link-${a.slug}`} onClick={() => navigate(`/kb/a/${a.slug}`)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm text-slate-700 dark:text-slate-200">{highlight(a.name)}</span>
                      {a.is_new && <KbNewPill />}
                      {a.is_snippet && <KbSnippetPill />}
                      {searchMode && a.category_name && <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-400">{a.category_name}</span>}
                      {a.is_draft && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Draft</span>}
                    </span>
                    {searchMode && a.snippet && <span className="mt-0.5 line-clamp-1 text-xs text-slate-400 dark:text-slate-500">{highlight(a.snippet)}</span>}
                  </span>
                  {a.has_table && <TableIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600" title="Contains tables" />}
                  {a.image_count > 0 && <ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600" title={`${a.image_count} images`} />}
                  <span className="mt-0.5 w-16 shrink-0 text-right text-[11px] text-slate-400 dark:text-slate-500">{a.hj_views.toLocaleString()} views</span>
                </button>
              )
            ))}
          </div>
        )}
      </div>
      )}

      {selectMode && (
        <div className="sticky bottom-4 z-10 mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 shadow-lg" data-testid="kb-move-bar">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{selected.size} selected</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="kb-move-target"
            className="h-9 min-w-[220px] flex-1 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-sm">
            <option value="">Move to category…</option>
            {allCats.filter((c) => c.cat_id !== data.category.cat_id).map((c) => (
              <option key={c.cat_id} value={c.cat_id}>{"— ".repeat(c.depth ? c.depth - 1 : 0)}{c.name.trim()}</option>
            ))}
          </select>
          <Button onClick={doMove} disabled={moving || !selected.size || !target} data-testid="kb-move-confirm"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderInput className="h-4 w-4" />} Move
          </Button>
        </div>
      )}
      {creating && tree && (
        <KbEditDialog open kind="article" mode="create" tree={tree}
          record={{ primary_category_id: category.cat_id }}
          onOpenChange={(v) => { if (!v) setCreating(false); }}
          onSaved={(a) => { setCreating(false); if (a?.slug) navigate(`/kb/a/${a.slug}`); }} />
      )}
      {editing && (
        <KbEditDialog open={editing} onOpenChange={setEditing} kind="category"
          record={{ ...category, scope: data.category.scope }}
          onSaved={(d) => setData({ ...data, category: { ...data.category, ...d } })} />
      )}
    </div>
  );
}
